import { Amplify } from '@aws-amplify/core';
import { generateClient } from 'aws-amplify/api';
import { 
	get as amplifyGet,
	post as amplifyPost, 
	del as amplifyDel
} from 'aws-amplify/api';
import { get as getLodash, isEmpty } from 'lodash-es';
import { API_NAME } from 'src/config/config';

import { getAccessToken, getGraphqlQueryString } from 'src/utils/commonFunctions';

// Lazy initialize client to avoid "Amplify has not been configured" error
// Client is only created when first accessed (after Amplify.configure is called)
let _client: any = null;
const getClient = () => {
	if (!_client) {
		_client = generateClient();
	}
	return _client;
};

// Create a proxy object that lazily initializes the client
// This allows existing code to use `client.graphql()` without changes
const clientProxy: any = new Proxy({}, {
    get(_target, prop) {
        const client = getClient();
        const value = client[prop];
        return typeof value === 'function' ? value.bind(client) : value;
    }
});

// const client = generateClient();

export const post = (params: {
	apiName: string,
	path: string,
	options?: any,
}) => {
	return amplifyPost(params).response;
};

export const del = (params: {
	apiName: string,
	path: string,
	options?: any
}) => {
	return amplifyDel(params).response;
};

export const get = (params: {
	apiName: string,
	path: string,
	options?: any
}) => {
	return amplifyGet(params).response;
};

export const executePostApi = async <T = any>(
    query: string,
    variables?: Record<string, any>
): Promise<T> => {
    const options = !isEmpty(variables) ? { body: variables } : {};
    try {
        const { body, statusCode } = await post({
            apiName: API_NAME,
            path: query,
            options
        });
        
        const responseText = await body.text();
        const responseBody = responseText ? JSON.parse(responseText) : null;
        
        if (statusCode !== 200) {
            const errorMessage = 
                (responseBody && typeof responseBody === 'object' && 'Messages' in responseBody)
                    ? responseBody.Messages || 'Unknown error'
                    : (typeof responseBody === 'string' ? responseBody : 'Unknown error');
            
            throw new Error(errorMessage);
        }
        
        return responseBody as T;
    } catch (error) {
        if (error instanceof SyntaxError) {
            throw new Error(`Failed to parse response as JSON: ${error.message}`);
        }
        throw error;
    }
};

const handleApiCall = async<T = any>(
	method: string,
	path: string,
	variables?: any,
	apiUrl?: string, // Regional API URL
	customHeaders?: any
): Promise<T> => {
	apiUrl ??= getLodash(Amplify.getConfig(), `API.REST.${API_NAME}.endpoint`, '');
	if (!apiUrl) {
        throw new Error(`Invalid api url`);
    }

	const accessToken = await getAccessToken();
	const requestInit: RequestInit = {
		method,
		headers: {
			'Content-Type': 'application/json',
			Authorization: accessToken,
			...(customHeaders || {}),
		},
	};
	if (variables && !isEmpty(variables)) {
		requestInit.body = JSON.stringify(variables);
	}

	const response: Response = await fetch(
        `${apiUrl}${path}`,
        requestInit
    );
    const responseText: string | null = await response.text();
	let parsedBody: T | null = null;

	// Only attempt to parse if there's content
	if (responseText) {
		try {
			parsedBody = JSON.parse(responseText) as T;
		} catch {
			throw new Error(`Invalid JSON response: ${responseText}`);
		}
	}

	if (!response.ok) {
		//const errorMessage = parsedBody && typeof parsedBody === 'object' && 'Messages' in parsedBody
		//	? (parsedBody as any).Messages || `HTTP Error ${response.status}`
		//	: responseText || `HTTP Error ${response.status}`;

		//throw new Error(errorMessage);

		// may be object or string
		const data = parsedBody;
		const error: Error = Object.assign(new Error, {
			response: {
				status: response.status,
				statusText: response.statusText,
				data // keeps { Messages: [...] }
			},
		});

		throw error;
	}

	return parsedBody as T;
}

export const delApi = async<T = any>(
	path: string,
	variables?: any
): Promise<T> => {
	return handleApiCall<T>('DELETE', path, variables);
};

export const delRegionalApi = async<T = any>(
	path: string,
	variables?: any,
	apiUrl?: string
): Promise<T> => {
	return handleApiCall<T>('DELETE', path, variables, apiUrl);
};

export const postRegionalApi = async<T = any>(
	path: string,
	variables?: any,
	apiUrl?: string
): Promise<T> => {
	return handleApiCall<T>('POST', path, variables, apiUrl);
};

export const getRegionalApi = async<T = any>(
	query: any,
	variables?: any,
	apiUrl?: string,
	customHeaders?: any
): Promise<T> => {
	const graphqlVariables: { query: any; variables?: any } = {
		query: getGraphqlQueryString(query),
	};
	if (variables) {
		graphqlVariables.variables = variables;
	}

	return handleApiCall<T>('POST', '/graphql', graphqlVariables, apiUrl, customHeaders);
};

// Export lazy-initialized client proxy as default
export default clientProxy;